// eslint-disable-next-line no-restricted-imports -- ghosts render the raw MUI overlays
import MuiDialog from "@mui/material/Dialog";
// eslint-disable-next-line no-restricted-imports -- ghosts render the raw MUI overlays
import MuiDrawer from "@mui/material/Drawer";
// eslint-disable-next-line no-restricted-imports -- ghosts render the raw MUI overlays
import MuiSnackbar from "@mui/material/Snackbar";
import { useTheme, type SxProps, type Theme } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import {
	GhostPaperProps,
	OverlayGhost as OverlayGhostModel,
	overlayGhostStore,
	OverlaySize,
} from "./overlay-ghost-store";

/** Removes a ghost whose exit transition never reports completion. */
const GHOST_SAFETY_TIMEOUT_MS = 2000;

const toSxArray = (sx?: SxProps<Theme>) => {
	if (!sx) return [];
	return Array.isArray(sx) ? sx : [sx];
};

const getGhostPaperProps = (
	paperProps: GhostPaperProps | undefined,
	{ width, height }: OverlaySize,
): GhostPaperProps => ({
	...paperProps,
	sx: [
		...toSxArray(paperProps?.sx),
		{ width: `${width}px`, height: `${height}px`, flex: "none" },
	],
});

const ghostRootSx = { pointerEvents: "none" } as const;

const GhostOverlay = ({
	ghost,
	onDone,
}: {
	ghost: OverlayGhostModel;
	onDone: () => void;
}) => {
	const theme = useTheme();
	const [open, setOpen] = useState(true);

	const handleRootRef = useCallback((root: HTMLElement | null) => {
		if (!root) return;
		// Commit the open styles first so the browser animates from them.
		void root.offsetHeight;
		requestAnimationFrame(() => setOpen(false));
	}, []);

	useEffect(() => {
		const timeout = setTimeout(onDone, GHOST_SAFETY_TIMEOUT_MS);
		return () => clearTimeout(timeout);
	}, [onDone]);

	const focusProps = {
		disableAutoFocus: true,
		disableEnforceFocus: true,
		disableEscapeKeyDown: true,
		disableRestoreFocus: true,
	};

	if (ghost.kind === "snackbar") {
		const { width, height } = ghost.size;
		const { transitionDuration } = ghost.props;
		return (
			<MuiSnackbar
				{...ghost.props}
				aria-hidden
				open={open}
				ref={handleRootRef}
				sx={[...toSxArray(ghost.props.sx), ghostRootSx]}
				// Snackbar renders nothing once closed unless its entry ran, so the ghost
				// enters instantly (already in place) instead of skipping the entry.
				transitionDuration={{
					enter: 0,
					exit:
						typeof transitionDuration === "number"
							? transitionDuration
							: (transitionDuration?.exit ??
								theme.transitions.duration.leavingScreen),
				}}
				TransitionProps={{ onExited: onDone }}>
				{/* The caller's surface classes restyle a blank box as the departed content. */}
				<div
					className={ghost.surface.className}
					style={{ width, height, boxSizing: "border-box" }}
				/>
			</MuiSnackbar>
		);
	}

	if (ghost.kind === "dialog") {
		return (
			<MuiDialog
				{...ghost.props}
				{...focusProps}
				aria-hidden
				open={open}
				ref={handleRootRef}
				sx={[...toSxArray(ghost.props.sx), ghostRootSx]}
				slotProps={{
					backdrop: {
						appear: false,
						transitionDuration: ghost.props.transitionDuration,
					},
				}}
				TransitionProps={{ appear: false, onExited: onDone }}
				PaperProps={getGhostPaperProps(ghost.props.PaperProps, ghost.size)}
			/>
		);
	}

	return (
		<MuiDrawer
			{...ghost.props}
			aria-hidden
			open={open}
			ref={handleRootRef}
			sx={[...toSxArray(ghost.props.sx), ghostRootSx]}
			ModalProps={focusProps}
			slotProps={{
				backdrop: {
					appear: false,
					transitionDuration: ghost.props.transitionDuration,
				},
			}}
			SlideProps={{ appear: false, onExited: onDone }}
			PaperProps={getGhostPaperProps(ghost.props.PaperProps, ghost.size)}
		/>
	);
};

/**
 * Renders the exit transition of overlays that were unmounted while open.
 * Mount once per app, inside the theme provider.
 */
export const OverlayGhostHost = () => {
	const [ghosts, setGhosts] = useState(overlayGhostStore.getGhosts);

	useEffect(
		() =>
			overlayGhostStore.subscribe((sync) => {
				const update = () => setGhosts(overlayGhostStore.getGhosts());
				if (sync) flushSync(update);
				else update();
			}),
		[],
	);

	return (
		<>
			{ghosts.map((ghost) => (
				<GhostOverlayItem
					key={ghost.key}
					ghost={ghost}
				/>
			))}
		</>
	);
};

const GhostOverlayItem = ({ ghost }: { ghost: OverlayGhostModel }) => {
	const handleDone = useCallback(
		() => overlayGhostStore.remove(ghost.key),
		[ghost.key],
	);

	return (
		<GhostOverlay
			ghost={ghost}
			onDone={handleDone}
		/>
	);
};
